import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { bloodGroups } from './bloodConfig';
import { makeId } from '../../utils/generateid';

export default function BloodSetEditor({
  data,
  onChange,
  showTimestampPicker = true,
  forcedTimestamp = null,
  buttonLabel = 'Add to Patient',
}) {
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [bloodData, setBloodData] = useState({});
  const [bloodsName, setBloodsName] = useState('');
  const [timestamp, setTimestamp] = useState(() =>
    forcedTimestamp ? dayjs(forcedTimestamp) : dayjs()
  );
  const [dateoffsetunits, setDateOffsetUnits] = useState('hours');
  const [dateoffset, setDateOffset] = useState(0);

  useEffect(() => {
    if (forcedTimestamp) setTimestamp(dayjs(forcedTimestamp));
  }, [forcedTimestamp]);

  const handleGroupSelection = (groups) => {
    setSelectedGroups(groups);

    const updatedData = { ...bloodData };

    groups.forEach((group) => {
      if (!updatedData[group]) {
        const normalPreset = bloodGroups[group].presets?.Normal || {};
        updatedData[group] = bloodGroups[group].elements.map((el) => ({
          ...el,
          value: normalPreset[el.name] ?? '',
        }));
      }
    });

    setBloodData(updatedData);
  };

  const handlePresetChange = (group, preset) => {
    const presetValues = bloodGroups[group].presets[preset] || {};
    const updatedElements = bloodGroups[group].elements.map((el) => ({
      ...el,
      value: presetValues[el.name] ?? el.value ?? '',
    }));
    setBloodData((prev) => ({ ...prev, [group]: updatedElements }));
  };

  const handleValueChange = (group, index, newValue) => {
    const updated = [...bloodData[group]];
    updated[index].value = newValue;
    setBloodData((prev) => ({ ...prev, [group]: updated }));
  };

  const handleSetUnits = (e) => {
    setDateOffsetUnits(e.target.value);
  };

  const handleSetOffset = (e) => {
    setDateOffset(e.target.value);
  };

  const handleRemoveGroup = (group) => {
    const newGroups = selectedGroups.filter((g) => g !== group);
    setSelectedGroups(newGroups);

    const newData = { ...bloodData };
    delete newData[group];
    setBloodData(newData);
  };

  const handleLocalSave = () => {
    const ts = forcedTimestamp ? dayjs(forcedTimestamp) : timestamp || dayjs();
    const timeKey = ts.toISOString();
    const name = bloodsName || null;

    const newEntry = {
      id: makeId(),
      name,
      timestamp: timeKey,
      time_offset: dateoffset,
      time_offset_unit: dateoffsetunits,
      groups: selectedGroups.map((groupKey) => ({
        groupKey,
        label: bloodGroups[groupKey].label,
        values: bloodData[groupKey],
      })),
    };

    const updated = [...(Array.isArray(data) ? data : []), newEntry];
    onChange?.(updated);

    setSelectedGroups([]);
    setBloodData({});
    setBloodsName('');
    if (!forcedTimestamp) setTimestamp(dayjs());
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: showTimestampPicker ? '300px 1fr' : '300px 1fr',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {showTimestampPicker && !forcedTimestamp ? (
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #d1d5db',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.1,
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Tooltip
                title="This sets the blood result time relative to the simulation time. For example, use 1 day or 24 hours to show results from the previous day."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon sx={{ fontSize: 18, cursor: 'help', color: 'text.secondary' }} />
              </Tooltip>

              <Typography sx={{ fontWeight: 700 }}>
                Time offset
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25} sx={{ p: 2, alignItems: 'center', marginTop:'10px' }}>
              <TextField
                size="small"
                type="number"
                label="Value"
                value={dateoffset ?? ''}
                onChange={handleSetOffset}
                sx={{ width: 110 }}
              />

              <Select
                value={dateoffsetunits}
                onChange={handleSetUnits}
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="minutes">Minutes</MenuItem>
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="months">Months</MenuItem>
              </Select>
            </Stack>
          </Paper>
        ) : (
          !showTimestampPicker && (
            <TextField
              label="Blood set name"
              type="text"
              value={bloodsName || ''}
              onChange={(e) => setBloodsName(e.target.value)}
              placeholder="Reference name for pushing live bloods"
              fullWidth
            />
          )
        )}

        <Paper
          elevation={0}
          sx={{
            border: '1px solid #d1d5db',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScienceOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography sx={{ fontWeight: 700 }}>
                Select blood groups
              </Typography>
            </Box>

            <Autocomplete
              multiple
              options={Object.keys(bloodGroups)}
              getOptionLabel={(key) => bloodGroups[key].label}
              value={selectedGroups}
              onChange={(e, newValue) => handleGroupSelection(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Blood groups"
                  placeholder="Search blood groups..."
                />
              )}
            />
          </Stack>
        </Paper>
      </Box>

      {selectedGroups.map((group) => (
        <Accordion
          key={group}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid #d1d5db',
            borderRadius: '12px !important',
            overflow: 'hidden',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e5e7eb',
              minHeight: 60,
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
                alignItems: 'center',
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                {bloodGroups[group].label}
              </Typography>


              <Box sx={{display:'flex', flexDirection:'row', justifyContent:'center', alignItems:'center'}}>
                <Typography sx={{fontWeight:300, fontSize:14}}>click to edit</Typography>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveGroup(group);
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Autocomplete
                options={Object.keys(bloodGroups[group].presets)}
                onChange={(e, newValue) => {
                  if (newValue) handlePresetChange(group, newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Preset"
                    placeholder="Search or select preset"
                  />
                )}
                defaultValue="Normal"
                sx={{ maxWidth: 280 }}
              />

              <Grid container spacing={1.5}>
                {bloodData[group]?.map((el, idx) => (
                  <Grid item xs={12} md={6} xl={4} key={el.name}>
                    <Paper
                      elevation={0}
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.25,
                        height: '100%',
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {el.name}
                        </Typography>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            gap: 1,
                            alignItems: 'center',
                          }}
                        >
                          <TextField
                            value={el.value}
                            onChange={(e) => handleValueChange(group, idx, e.target.value)}
                            size="small"
                            label="Value"
                            fullWidth
                          />

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 52 }}
                          >
                            {el.units}
                          </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                          Ref: {el.refLow} – {el.refHigh}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
        <Button
          variant="contained"
          onClick={handleLocalSave}
          disabled={selectedGroups.length === 0 || (!showTimestampPicker && !bloodsName)}
          startIcon={<AddCircleOutlineIcon />}
          sx={{
            textTransform: 'none',
            minWidth: 180,
          }}
        >
          {buttonLabel}
        </Button>
      </Box>
    </Box>
  );
}